/**
 * QR Code Scan Handler Page
 * Processes QR code scans and redirects/displays appropriate content
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, UserPlus, Users, Calendar, QrCode } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import PhoneInput from '@/components/ui/PhoneInput';
interface QRScanResponse {
  status: string;
  scan_id?: string;
  qr_type?: string;
  organization_id?: number;
  redirect_url?: string;
  actions?: string[];
  custom_fields?: Record<string, any>;
  referral_owner_type?: string;
  referral_owner_id?: string;
  error?: string;
  code?: string;
}

export default function QRScanPage() {
  const { t } = useAppTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<QRScanResponse | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (token) {
      processScan(token);
    } else {
      setError('No QR code token provided');
      setLoading(false);
    }
  }, [token]);

  const processScan = async (qrToken: string) => {
    try {
      setLoading(true);

      // No location request. This used to ask every scanner for their device
      // location — and then never send it, since this GET carries no body. For
      // a QR on a flyer for an anonymous survey, a location prompt is exactly
      // the wrong first impression, so it is gone (TRUS-624). The backend also
      // stores no IP or coordinates for public-form QR codes.

      // Call backend to process scan
      const response = await fetch(
        getWeaveinApiUrl(`/api/qr-codes/token/${qrToken}`),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data: QRScanResponse = await response.json();
      setScanResult(data);

      if (data.status === 'error') {
        setError(data.error || 'Failed to process QR code');
      } else if (data.status === 'success') {
        // Handle different QR types
        handleScanSuccess(data);
      }
    } catch (err) {
      console.error('Error processing QR scan:', err);
      setError('Failed to process QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleScanSuccess = (data: QRScanResponse) => {
    // Check actions to determine what to show
    if (data.qr_type === 'member_onboarding' || data.actions?.includes('show_member_registration_form')) {
      // TRUS-778: a member-onboarding QR (a flyer) goes to self sign-up, which
      // creates the account and the request. The form that used to render here
      // posted to an endpoint that saved nothing. The token rides along so the
      // request is attributed to the organization that printed the flyer.
      navigate(`/member/join?qr=${encodeURIComponent(token || '')}`, { replace: true });
    } else if (data.actions?.includes('show_user_registration_form')) {
      // Redirect to user registration with QR token
      navigate(`/register?qr=${token}`);
    } else if (data.actions?.includes('show_enrollment_form')) {
      // Redirect to enrollment page
      navigate(`/member/register/${token}`);
    } else if (data.actions?.includes('show_checkin_form')) {
      setShowForm(true);
    } else if (data.redirect_url) {
      // External redirect
      window.location.href = data.redirect_url;
    }
  };

  const getQRTypeIcon = (qrType?: string) => {
    switch (qrType) {
      case 'member_onboarding':
        return <UserPlus className="h-12 w-12 text-blue-500" />;
      case 'user_invitation':
        return <Users className="h-12 w-12 text-purple-500" />;
      case 'program_enrollment':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'event_checkin':
        return <Calendar className="h-12 w-12 text-orange-500" />;
      default:
        return <QrCode className="h-12 w-12 text-gray-500" />;
    }
  };

  const getQRTypeTitle = (qrType?: string) => {
    switch (qrType) {
      case 'member_onboarding':
        return 'Member Registration';
      case 'user_invitation':
        return 'Staff Invitation';
      case 'program_enrollment':
        return 'Program Enrollment';
      case 'event_checkin':
        return 'Event Check-in';
      default:
        return 'QR Code Scanned';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">{t('qr.processing_qr_code', 'Processing QR Code')}</h2>
          <p className="text-gray-500 dark:text-gray-400">{t('qr.please_wait_while_we_verify', 'Please wait while we verify your scan...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">{t('qr.qr_code_error', 'QR Code Error')}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('qr.return_home', 'Return Home')}
          </button>
        </div>
      </div>
    );
  }

  // Show appropriate form based on QR type
  if (showForm && scanResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            {getQRTypeIcon(scanResult.qr_type)}
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mt-4">
              {getQRTypeTitle(scanResult.qr_type)}
            </h2>
          </div>

          {scanResult.qr_type === 'event_checkin' && (
            <EventCheckinForm
              organizationId={scanResult.organization_id!}
              qrToken={token!}
              onSuccess={() => {
                setScanResult(prev => prev ? { ...prev, status: 'completed' } : null);
                setShowForm(false);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // Success state after form submission
  if (scanResult?.status === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{t('qr.success', 'Success!')}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {scanResult.qr_type === 'member_onboarding'
              ? 'Your registration has been submitted successfully.'
              : scanResult.qr_type === 'event_checkin'
              ? 'You have been checked in successfully.'
              : 'Your request has been processed.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('qr.return_home_1', 'Return Home')}
          </button>
        </div>
      </div>
    );
  }

  // Default success state (for cases without forms)
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {getQRTypeIcon(scanResult?.qr_type)}
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mt-4 mb-2">
          {getQRTypeTitle(scanResult?.qr_type)}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{t('qr.qr_code_scanned_successfully', 'QR code scanned successfully!')}</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('qr.return_home_2', 'Return Home')}
        </button>
      </div>
    </div>
  );
}

// Event Check-in Form Component
function EventCheckinForm({
  organizationId,
  qrToken,
  onSuccess
}: {
  organizationId: number;
  qrToken: string;
  onSuccess: () => void;
}) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/qr-codes/scan'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            qr_token: qrToken,
            form_data: {
              name: name
            }
          })
        }
      );

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to check in');
      }
    } catch (err) {
      setError('Failed to check in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('qr.your_name', 'Your Name *')}
        </label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          placeholder={t('qr.enter_your_name', 'Enter your name')}
        />
      </div>

      {error && (
        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
      >
        {submitting ? (
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        ) : (
          'Check In'
        )}
      </button>
    </form>
  );
}
